/*
Functions that are common to multiple components (and even multiple projects)
 */

import errorFunctions from "./errorFunctions";

let utilityFunctions = {
  generateUUID: function () {
    let d = new Date().getTime();
    if (
      typeof performance !== "undefined" &&
      typeof performance.now === "function"
    ) {
      d += performance.now(); //use high-precision timer if available
    }
    return "xxxxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = ((d + Math.random() * 16) % 16) | 0;
      d = Math.floor(d / 16);
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  },
  returnNestedOrFail: function (obj /*, level1, level2, ... levelN*/) {
    //If looking for a value in a sub-object, you need to check the validity of all the parent objects first. This function allows you to skip the validity check
    let args = Array.prototype.slice.call(arguments, 1);

    for (var i = 0; i < args.length; i++) {
      // eslint-disable-next-line no-prototype-builtins
      if (!obj || !obj.hasOwnProperty(args[i])) {
        return null;
      }
      obj = obj[args[i]];
    }
    return obj;
  },
  setCookie: function (cname, cvalue, exdays) {
    var expires = "";
    if (exdays) {
      var d = new Date();
      d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
      expires = "expires=" + d.toUTCString();
    }
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
  },
  toTitleCase: function (str) {
    return str?.replace(
      /\w\S*/g,
      (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
    );
  },
  decodeCookieValue: function (value) {
    //document.cookie is a shared namespace, and neither we nor the third-party
    //scripts on the page are obliged to percent-encode what we write. A stray
    //"%" that isn't a valid escape sequence makes decodeURIComponent throw a
    //URIError, so an undecodable value falls back to its raw text.
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  },
  getCookie: function (cname) {
    //Split first and only decode the value we were asked for - decoding the
    //whole document.cookie string let one unrelated malformed cookie throw and,
    //because this runs in the App constructor, take the entire app down.
    var name = cname + "=";
    var ca = (document.cookie || "").split(";");
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return utilityFunctions.decodeCookieValue(
          c.substring(name.length, c.length),
        );
      }
    }
    return "";
  },
  getCookieAsJson: function (cname) {
    //A cookie can exist with an empty or truncated value (e.g. after a logout
    //that blanked it instead of expiring it), so the parse must never be assumed
    //to succeed - callers get null rather than a thrown SyntaxError.
    var raw = utilityFunctions.getCookie(cname);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error(`Error parsing cookie "${cname}" as JSON:`, error);
      return null;
    }
  },
  getUserInfo: function () {
    //Single safe reader for the stored session. Returns null when the user is
    //logged out, when the session was only stored in one of the two locations,
    //or when the stored value isn't valid JSON - callers must never assume a shape.
    try {
      const userInfoJson =
        localStorage.getItem("userInfo") ||
        utilityFunctions.getCookie("userInfo");
      if (!userInfoJson) return null;
      return JSON.parse(userInfoJson);
    } catch (error) {
      console.error("Error parsing user info JSON:", error);
      return null;
    }
  },
  getAccessToken: function () {
    //Single safe reader for the bearer token. Returns null when there is no
    //usable session instead of throwing on a blank/corrupt stored value.
    return utilityFunctions.getUserInfo()?.access_token || null;
  },
  clearUserInfo: function () {
    //Expire the cookie instead of blanking it - a cookie left as "userInfo="
    //is still present on every later request and breaks any JSON.parse of it.
    utilityFunctions.setCookie("userInfo", "", -20);
    localStorage.removeItem("userInfo");
  },
  validateEmail: function (email) {
    //https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
    //Haven't verified this works yet since I haven't used this function yet - not implemented in this project yet

    //eslint-disable
    const re =
      /^(([^<>()[\]\\.,;:\s@]+(\.[^<>()[\]\\.,;:\s@]+)*))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  },
  dataURLtoFile: function (dataurl, filename) {
    var arr = dataurl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  },
  downloadFileAutomatically: function (file, fileType) {
    if (window.navigator.msSaveBlob) {
      window.navigator.msSaveBlob(file, `file.${fileType}`);
    } else {
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display: none";
      const url = window.URL.createObjectURL(file);
      a.href = url;

      let type = "";
      switch (fileType) {
        case "application/pdf":
          type = "pdf";
          break;
        case "image/png":
          type = "png";
          break;
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          type = "xlsx";
          break;
        case "text/xlsx":
          type = "xlsx";
          break;
        default:
          type = "jpg";
      }
      a.download = `${file.name}.${type}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setTimeout(function () {
        document.body.removeChild(a);
      }, 1000);
    }
  },

  timeDifferenceBetweenTwoTimes: function (timeA, timeB) {
    //create date format - note date is dummy and is irrelevant
    const timeStart = new Date("01/01/2007 " + timeA);
    const timeEnd = new Date("01/01/2007 " + timeB);

    let hourDifference = (timeEnd - timeStart) / 60 / 60 / 1000;

    return hourDifference;
  },
  //Below gets 1st, 2nd, 3rd etc from a number
  //Source: https://stackoverflow.com/questions/13627308/add-st-nd-rd-and-th-ordinal-suffix-to-a-number
  ordinal_suffix_of: function (i) {
    let j = i % 10,
      k = i % 100;
    if (j === 1 && k !== 11) {
      return i + "st";
    }
    if (j === 2 && k !== 12) {
      return i + "nd";
    }
    if (j === 3 && k !== 13) {
      return i + "rd";
    }
    return i + "th";
  },
  sortArrayByObjectProperty: function (array, propertyName, sortType) {
    function compare(a, b) {
      // Get nulls and falses alw
      if (a[propertyName] === null || a[propertyName] === undefined) {
        return 1;
      } else if (b[propertyName] === null || b[propertyName] === undefined) {
        return -1;
      } else if (a[propertyName] < b[propertyName]) {
        return -1 * sortType;
      } else if (a[propertyName] > b[propertyName]) {
        return 1 * sortType;
      }
      return 0;
    }
    array.sort(compare);

    return array;
  },
  cloneObject: function (obj) {
    var copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || "object" != typeof obj) return obj;

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (var i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.cloneObject(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (var attr in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(attr)) copy[attr] = this.cloneObject(obj[attr]);
      }
      return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
  },
  protectedFetch: function (url, method, data, checkFunction, formData) {
    const token =
      this?.state?.userInfo?.access_token || utilityFunctions.getAccessToken();

    const logout = () => {
      //protectedFetch is also called unbound (utilityFunctions.protectedFetch),
      //so the component callbacks are only available when a component bound it.
      this?.userHasAuthenticated?.(false);
      this?.setState?.({ userInfo: null });
      utilityFunctions.clearUserInfo();
      window.location.href = "/";
    };

    // Validate method
    const validMethods = ["GET", "POST", "PUT", "PATCH", "DELETE"];
    if (!validMethods.includes(method.toUpperCase())) {
      return Promise.reject({
        type: "INVALID_METHOD",
        message: `Invalid HTTP method: ${method}. Must be one of: ${validMethods.join(", ")}`,
      });
    }

    return new Promise(function (resolve, reject) {
      let options = {
        method: method,
        headers: {
          Authorization: "Bearer " + token,
        },
        timeout: 30000, // 30 seconds timeout
      };

      // Handle different methods appropriately
      switch (method.toUpperCase()) {
        case "GET":
          if (data) {
            const queryParams = new URLSearchParams(data);
            url = `${url}?${queryParams.toString()}`;
          }
          break;

        case "POST":
        case "PUT":
        case "PATCH":
          if (formData) {
            options.body = data;
          } else {
            options.headers["Content-Type"] = "application/json";
            options.body = JSON.stringify(data || {});
          }
          break;

        case "DELETE":
          if (data) {
            options.headers["Content-Type"] = "application/json";
            options.body = JSON.stringify(data);
          }
          break;
      }

      const maxRetries = 3;
      let retryCount = 0;

      const attemptFetch = () => {
        fetch(url, options)
          .then(async (response) => {
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              const errorMessage = errorFunctions.toErrorMessage(
                errorData,
                response.statusText,
              );
              // Field-level map so a form can show the error against the input
              // that caused it, not only in a toast.
              const fieldErrors = errorFunctions.parseApiErrors(errorData);

              switch (response.status) {
                case 400:
                  reject({
                    type: "BAD_REQUEST",
                    message: errorMessage,
                    errors: fieldErrors,
                    status: 400,
                  });
                  break;
                case 401:
                  reject({
                    type: "UNAUTHORIZED",
                    message: "Session expired. Please login again.",
                  });
                  logout();
                  break;
                case 403:
                  reject({
                    type: "FORBIDDEN",
                    message:
                      "You do not have permission to perform this action.",
                  });
                  logout();
                  break;
                case 404:
                  reject({
                    type: "NOT_FOUND",
                    message: "The requested resource was not found.",
                  });
                  break;
                case 409:
                  reject({
                    type: "CONFLICT",
                    message: "Resource conflict detected.",
                  });
                  break;
                case 413:
                  reject({
                    type: "NOT_VERIFIED",
                    message: "Your account needs to be verified.",
                  });
                  break;
                case 422:
                  reject({
                    type: "VALIDATION_ERROR",
                    message: errorMessage,
                    errors: fieldErrors,
                    status: 422,
                  });
                  break;
                case 429:
                  if (retryCount < maxRetries) {
                    retryCount++;
                    const retryAfter =
                      response.headers.get("Retry-After") || 1000;
                    setTimeout(attemptFetch, retryAfter);
                    return;
                  }
                  reject({
                    type: "TOO_MANY_REQUESTS",
                    message: "Too many requests. Please try again later.",
                  });
                  break;
                case 500:
                case 502:
                case 503:
                case 504:
                  if (retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(attemptFetch, 1000 * Math.pow(2, retryCount));
                    return;
                  }
                  reject({
                    type: "SERVER_ERROR",
                    message: "Server error. Please try again later.",
                  });
                  break;
                default:
                  reject({
                    type: "UNKNOWN_ERROR",
                    message: `An error occurred: ${response.status} ${response.statusText}`,
                  });
              }
              return null;
            }
            return checkFunction(response);
          })
          .then((response) => {
            if (response) {
              resolve(response);
            } else {
              reject({
                type: "CHECK_FUNCTION_FAILED",
                message: "Failed to process response",
              });
            }
          })
          .catch((error) => {
            console.error("Fetch error:", error);

            if (error.name === "TypeError" && retryCount < maxRetries) {
              retryCount++;
              setTimeout(attemptFetch, 1000 * Math.pow(2, retryCount));
              return;
            }

            if (error.type) {
              reject(error);
            } else if (error.name === "TypeError") {
              reject({
                type: "NETWORK_ERROR",
                message:
                  "Unable to connect to the server. Please check your internet connection.",
              });
            } else if (error.name === "AbortError") {
              reject({
                type: "REQUEST_ABORTED",
                message: "Request was cancelled",
              });
            } else if (error.name === "SyntaxError") {
              reject({
                type: "INVALID_JSON",
                message: "Invalid response from server",
              });
            } else {
              reject({
                type: "UNKNOWN_ERROR",
                message: `An unexpected error occurred: ${error.message}`,
              });
            }
          });
      };

      attemptFetch();
    });
  },
};

const BRAND_CODE = {
  DREAME: "DREAME",
  STEELSERIES: "STEEL",
};

export const isSuperAdminRole = (email) =>
  ["william@treeclouds.com", "alfred@subemo.com"].includes(email);

export const isSteelSeriesBrand = (brandCode) =>
  brandCode === BRAND_CODE.STEELSERIES;
export const isDreameBrand = (brandCode) => brandCode === BRAND_CODE.DREAME;

// RMAS-137: display text for a raw ticket status, from the detail response's
// `status_labels` map. Display only - always send the raw value back to the
// API. Falls back to the raw value when the response has no label for it.
export const getStatusLabel = (ticket, status) =>
  (ticket && ticket.status_labels && ticket.status_labels[status]) || status;

export default utilityFunctions;
